// Écrans plein canvas : panneau central (menu, mort, victoire, game over) et menu
// clavier + tactile. Tout est peint sur le canvas (aucun DOM), typo monospace,
// textes en français, aucun tiret cadratin. Palette alignée sur le rendu du jeu.
import { isTouch } from './platform'

const BG = '#0d0b1a'
const PANEL = '#1a1633'
const FG = '#f0e9d2'
const ACCENT = '#ffd93d'
const DIM = '#7a7599'
const FONT = 'ui-monospace, Menlo, monospace'

// Panneau centré sur fond assombri. Le panneau se dimensionne pour contenir le
// texte le plus large (titre ou sous-titre), avec des marges généreuses.
export function drawCenterScreen(ctx: CanvasRenderingContext2D, title: string, subtitle: string): void {
  const W = ctx.canvas.width, H = ctx.canvas.height
  ctx.save()
  ctx.fillStyle = 'rgba(13, 11, 26, 0.82)'
  ctx.fillRect(0, 0, W, H)

  const titleFont = `bold 52px ${FONT}`
  const subFont = `26px ${FONT}`
  ctx.font = titleFont
  const tw = ctx.measureText(title).width
  ctx.font = subFont
  const sw = ctx.measureText(subtitle).width
  const pw = Math.min(W - 48, Math.max(360, Math.max(tw, sw) + 96))
  const ph = 200
  const px = (W - pw) / 2, py = (H - ph) / 2

  ctx.fillStyle = PANEL
  ctx.fillRect(px, py, pw, ph)
  ctx.strokeStyle = ACCENT
  ctx.lineWidth = 3
  ctx.strokeRect(px + 1.5, py + 1.5, pw - 3, ph - 3)

  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillStyle = ACCENT
  ctx.font = titleFont
  ctx.fillText(title, W / 2, py + ph * 0.38)
  ctx.fillStyle = FG
  ctx.font = subFont
  ctx.fillText(subtitle, W / 2, py + ph * 0.68)
  ctx.restore()
}

// Bandeau LÉGER (16/07 soir) : une bande semi-transparente + un texte, SANS voile plein écran —
// pour les moments où la partie continue de vivre derrière (réanimation au ver checkpoint).
export function drawBanner(ctx: CanvasRenderingContext2D, text: string): void {
  const W = ctx.canvas.width
  ctx.save()
  ctx.fillStyle = 'rgba(13, 11, 26, 0.6)'
  ctx.fillRect(0, 84, W, 56)
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillStyle = '#f7c4ae' // rose du ver : le bandeau annonce sa réanimation
  ctx.font = `bold 28px ${FONT}`
  ctx.fillText(text, W / 2, 112)
  ctx.restore()
}

export interface MenuHandle { destroy(): void }

// Menu dessiné sur le canvas : haut/bas pour choisir, Entrée pour valider ; au
// tactile (ou à la souris), un clic sur une entrée la choisit et la valide.
// Escape n'est PAS géré ici (ignoré au menu racine ; l'appelant peut ajouter son
// propre retour pour un sous-menu). destroy() détache les écouteurs (clavier, clic).
// subtitle (optionnel) : une ligne sous le titre (ex. « Bonjour Papa » une fois connecté,
// ou « Hors ligne (scores non enregistrés) ») ; redessinée à chaque navigation.
interface Box { x0: number; y0: number; x1: number; y1: number }

export function makeMenu(
  canvas: HTMLCanvasElement,
  entries: string[],
  onPick: (i: number) => void,
  subtitle?: string,
  onBack?: () => void, // sous-écran : flèche « ← Retour » (tactile) + Échap
): MenuHandle {
  const ctx = canvas.getContext('2d')!
  let sel = 0
  let scrollTop = 0 // index de la 1re entrée visible (défilement pour les longues listes)
  // Hauteur de rangée ADAPTATIVE BORNÉE (audit 16/07, chantier B) : un menu COURT (l'accueil
  // connecté = 8 entrées) tient EN ENTIER avec de grandes cibles tactiles (jusqu'à 66px) ;
  // une liste longue garde une rangée lisible (>= 46px) et défile comme avant. La bande est
  // élargie (0.36H..0.90H) : le titre s'arrête à ~0.32H, la ligne d'aide vit à 0.94H.
  const band = (): { top: number; bottom: number } => ({ top: canvas.height * 0.36, bottom: canvas.height * 0.9 })
  const rowH = (): number => {
    const b = band()
    return Math.max(46, Math.min(66, Math.floor((b.bottom - b.top) / Math.max(1, entries.length))))
  }
  const maxVisible = (): number => Math.max(1, Math.floor((band().bottom - band().top) / rowH()))
  // Garde la sélection dans la fenêtre visible et borne le défilement.
  function clampScroll(): void {
    const mv = maxVisible()
    if (sel < scrollTop) scrollTop = sel
    else if (sel >= scrollTop + mv) scrollTop = sel - mv + 1
    scrollTop = Math.max(0, Math.min(scrollTop, Math.max(0, entries.length - mv)))
  }
  // Zone tactile GÉNÉREUSE, jusqu'au coin haut-gauche (cible facile au doigt) : plus large que le
  // texte « ← Retour » (dessiné à x=18, y=37), sans chevaucher le titre (centré) ni les entrées (>= 0.4 H).
  const backBox = (): Box => ({ x0: 0, y0: 0, x1: 210, y1: 78 })
  const scrollBtns = (): { up: Box; down: Box } => {
    const b = band(), W = canvas.width
    return { up: { x0: W - 96, y0: b.top, x1: W - 16, y1: b.top + 54 }, down: { x0: W - 96, y0: b.bottom - 54, x1: W - 16, y1: b.bottom } }
  }
  const inBox = (x: number, y: number, b: Box): boolean => x >= b.x0 && x < b.x1 && y >= b.y0 && y < b.y1

  function draw(): void {
    const W = canvas.width, H = canvas.height
    ctx.fillStyle = BG
    ctx.fillRect(0, 0, W, H)
    ctx.textBaseline = 'middle'

    ctx.textAlign = 'center'
    ctx.fillStyle = ACCENT
    ctx.font = `bold 76px ${FONT}`
    ctx.fillText("MAD'TABORNIO", W / 2, H * 0.22)
    if (subtitle) {
      ctx.fillStyle = DIM
      ctx.font = `26px ${FONT}`
      ctx.fillText(subtitle, W / 2, H * 0.32)
    }

    // Flèche retour (sous-écran seulement).
    if (onBack) {
      const bb = backBox()
      ctx.textAlign = 'left'
      ctx.fillStyle = ACCENT
      ctx.font = `30px ${FONT}`
      ctx.fillText('← Retour', bb.x0 + 6, (bb.y0 + bb.y1) / 2)
    }

    clampScroll()
    const b = band(), mv = maxVisible(), rh = rowH()
    // Bloc centré verticalement dans la bande quand tout tient (sinon collé en haut + défilement).
    const y0 = b.top + (entries.length <= mv ? Math.max(0, (b.bottom - b.top - entries.length * rh) / 2) : 0)
    ctx.textAlign = 'center'
    ctx.font = `${rh >= 52 ? 32 : 28}px ${FONT}` // typo liée à la rangée, jamais illisible
    for (let k = 0; k < mv; k++) {
      const i = scrollTop + k
      if (i >= entries.length) break
      const y = y0 + rh / 2 + k * rh
      ctx.fillStyle = i === sel ? ACCENT : FG
      ctx.fillText(`${i === sel ? '> ' : ''}${entries[i]}`, W / 2, y)
    }

    // Indicateurs / boutons de défilement (tactile) : n'apparaissent que s'il y a à défiler.
    const sb = scrollBtns()
    ctx.fillStyle = ACCENT
    ctx.font = `36px ${FONT}`
    if (scrollTop > 0) ctx.fillText('▲', (sb.up.x0 + sb.up.x1) / 2, (sb.up.y0 + sb.up.y1) / 2)
    if (scrollTop + mv < entries.length) ctx.fillText('▼', (sb.down.x0 + sb.down.x1) / 2, (sb.down.y0 + sb.down.y1) / 2)

    ctx.fillStyle = DIM
    ctx.font = `22px ${FONT}`
    const scrollHint = entries.length > mv ? (isTouch ? ' · ▲▼ pour défiler' : ' · molette pour défiler') : ''
    const backHint = onBack ? (isTouch ? ' · ← pour revenir' : ' · Échap pour revenir') : ''
    ctx.fillText((isTouch ? 'Touche une entrée' : 'Flèches + Entrée') + scrollHint + backHint, W / 2, H * 0.94)
  }

  function onKey(e: KeyboardEvent): void {
    if (e.code === 'ArrowUp') { sel = Math.max(0, sel - 1); draw(); e.preventDefault() }
    else if (e.code === 'ArrowDown') { sel = Math.min(entries.length - 1, sel + 1); draw(); e.preventDefault() }
    else if (e.code === 'Enter' || e.code === 'NumpadEnter') { e.preventDefault(); onPick(sel) }
    else if (e.code === 'Escape' && onBack) { e.preventDefault(); onBack() }
  }

  // Coordonnées converties à l'échelle du canvas (affiché en CSS à une autre taille).
  function toCanvas(e: MouseEvent): { x: number; y: number } | null {
    const r = canvas.getBoundingClientRect()
    if (r.width === 0 || r.height === 0) return null
    return { x: ((e.clientX - r.left) / r.width) * canvas.width, y: ((e.clientY - r.top) / r.height) * canvas.height }
  }
  function onClick(e: MouseEvent): void {
    const p = toCanvas(e)
    if (!p) return
    if (onBack && inBox(p.x, p.y, backBox())) { e.preventDefault(); onBack(); return }
    const sb = scrollBtns(), mv = maxVisible()
    if (scrollTop > 0 && inBox(p.x, p.y, sb.up)) { e.preventDefault(); sel = Math.max(0, sel - mv); draw(); return }
    if (scrollTop + mv < entries.length && inBox(p.x, p.y, sb.down)) { e.preventDefault(); sel = Math.min(entries.length - 1, sel + mv); draw(); return }
    const b = band(), rh = rowH()
    const y0 = b.top + (entries.length <= mv ? Math.max(0, (b.bottom - b.top - entries.length * rh) / 2) : 0)
    for (let k = 0; k < mv; k++) {
      const i = scrollTop + k
      if (i >= entries.length) break
      const cy = y0 + rh / 2 + k * rh
      if (p.y >= cy - rh / 2 && p.y < cy + rh / 2) { e.preventDefault(); sel = i; draw(); onPick(i); return }
    }
  }
  function onWheel(e: WheelEvent): void {
    sel = Math.max(0, Math.min(entries.length - 1, sel + (e.deltaY > 0 ? 1 : -1)))
    draw()
    e.preventDefault()
  }

  addEventListener('keydown', onKey)
  canvas.addEventListener('click', onClick)
  canvas.addEventListener('wheel', onWheel, { passive: false })
  draw()
  return {
    destroy() {
      removeEventListener('keydown', onKey)
      canvas.removeEventListener('click', onClick)
      canvas.removeEventListener('wheel', onWheel)
    },
  }
}
